import { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router';
import { LastWatchedExt, MediaType } from '@shared/types';
import { userAtom } from '@app/atoms/userAtom';
import { getProgress } from '@app/utils/api';
import { buildShowIdentifier, collectShowIdentifiers, isYoutubeShow } from '@shared/utils/show';
import { selectedEpisodeAtom, selectedMediaAtom, selectedSeasonAtom } from '@app/atoms/selectedMediaAtoms';
import { getWordsDataAtomDefaults, wordsDataAtom, wordsErrorAtom } from '@app/atoms/wordsAtoms';
import SearchPanel from '@app/components/SearchPanel';
import MediaInfo from '@app/components/MediaInfo';
import { PageId } from '../types';
import { getProgressPercent, isProgressLoading, isSameProgressScope } from '@app/utils/progress';
import { loadingProgressItemAtom, loadingShowIdsAtom, localLoadingProgressItemAtom } from '@app/atoms/uiAtoms';
import './SearchPage.scss';

interface MediaHubPageProps {
  pageId: PageId.SERIES | PageId.MOVIES;
}

const MediaHubPage = ({ pageId }: MediaHubPageProps) => {
  const user = useAtomValue(userAtom);
  const navigate = useNavigate();
  const setSelectedMedia = useSetAtom(selectedMediaAtom);
  const setSelectedSeason = useSetAtom(selectedSeasonAtom);
  const setSelectedEpisode = useSetAtom(selectedEpisodeAtom);
  const setWordsData = useSetAtom(wordsDataAtom);
  const setWordsError = useSetAtom(wordsErrorAtom);
  const loadingShowIds = useAtomValue(loadingShowIdsAtom);
  const loadingProgressItem = useAtomValue(loadingProgressItemAtom);
  const localLoadingProgressItem = useAtomValue(localLoadingProgressItemAtom);
  const [recentItems, setRecentItems] = useState<LastWatchedExt[]>([]);

  const targetType = useMemo(() => (
    pageId === PageId.SERIES ? MediaType.TVSHOW : MediaType.MOVIE
  ), [pageId]);

  const clearSelectionContext = () => {
    setSelectedMedia(null);
    setSelectedSeason(null);
    setSelectedEpisode(null);
    setWordsData(getWordsDataAtomDefaults());
    setWordsError('');
  };

  const navigateToDetail = (
    item: LastWatchedExt | { show: any; season?: number; episode?: number },
    options: { forceSeasonPicker?: boolean } = {}
  ) => {
    const showKey = buildShowIdentifier(item.show);
    if (!showKey) {
      return;
    }

    setSelectedMedia(item.show);

    let selectedSeason = null;
    let selectedEpisode = null;

    if (item.season != null && item.show?.seasons) {
      selectedSeason = item.show.seasons.find((s: any) => s.num === item.season) || null;
      if (selectedSeason && item.episode != null) {
        selectedEpisode = selectedSeason.episodes.find((e: any) => e.num === item.episode) || null;
      }
    }

    setSelectedSeason(selectedSeason);
    setSelectedEpisode(selectedEpisode);

    const targetPage = isYoutubeShow(item.show)
      ? PageId.YOUTUBE
      : (item.show?.type === MediaType.TVSHOW ? PageId.SERIES : PageId.MOVIES);
    navigate(`/${targetPage}/${encodeURIComponent(showKey)}`, {
      state: options.forceSeasonPicker ? { forceSeasonPicker: true } : undefined
    });
  };

  useEffect(() => {
    clearSelectionContext();
  }, [pageId]);

  useEffect(() => {
    if (!user) {
      setRecentItems([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await getProgress(1, 30);
        if (cancelled) return;
        const filtered = (resp.list ?? []).filter((item) => {
          if (!item.show) {
            return false;
          }
          if (pageId === PageId.MOVIES) {
            return item.show.type === MediaType.MOVIE && !isYoutubeShow(item.show);
          }
          return item.show.type === targetType;
        });
        setRecentItems(filtered.slice(0, 5));
      } catch {
        if (!cancelled) {
          setRecentItems([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, targetType]);

  const getSeasonEpisodeFromItem = (item: LastWatchedExt) => {
    const show: any = item.show;
    if (!show?.seasons) return { season: undefined, episode: undefined };

    const season = item.season != null
      ? show.seasons.find((s: any) => s.num === item.season)
      : undefined;

    const episode = season && item.episode != null
      ? season.episodes.find((e: any) => e.num === item.episode)
      : undefined;

    return { season, episode };
  };
  const isItemLoading = (item: LastWatchedExt) => {
    if (isProgressLoading(item)) {
      return true;
    }
    if (loadingProgressItem || localLoadingProgressItem) {
      return isSameProgressScope(item, loadingProgressItem || localLoadingProgressItem);
    }
    const ids = collectShowIdentifiers(item.show as any, [item.screenmediaId]);
    return ids.some((id) => loadingShowIds.has(id));
  };

  return (
    <div className="container">
      <SearchPanel
        pageId={pageId}
        onSelectMedia={(media) => navigateToDetail({ show: media }, { forceSeasonPicker: true })}
      />

      {recentItems.length > 0 && (
        <div className="episode-progress-panel border rounded p-3 mt-3">
          <div className="fw-semibold mb-2">
            {pageId === PageId.SERIES ? 'Recently viewed TV shows' : 'Recently viewed movies'}
          </div>
          <ListGroup>
            {recentItems.map((item) => {
              const { season, episode } = getSeasonEpisodeFromItem(item);
              return (
                <ListGroup.Item key={item.id} className="p-0 border-0 mb-2">
                  <Button
                    variant="outline-secondary"
                    className="d-flex w-100"
                    onClick={() => navigateToDetail(item)}
                  >
                    <MediaInfo
                      media={item.show as any}
                      season={season}
                      episode={episode}
                      seasonNum={item.season}
                      episodeNum={item.episode}
                      progressPercent={getProgressPercent(item)}
                      isLoading={isItemLoading(item)}
                    />
                  </Button>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

export default MediaHubPage;
